import { ISimplePromotionConfiguration } from '@barkinglabs/promotions-types';

/**
 * Resolves the coupon code from a promotion configuration. `localizedText[]`
 * is the source of truth — that's what fi-admin writes today and what the
 * banner reads. The top-level `configuration.couponCode` is a DEPRECATED
 * legacy field (admin no longer surfaces it since barkinglabs/admin#1715,
 * 2026-03-02), preserved only as a safety net for older promotions.
 *
 * All new code that needs a promo's coupon MUST go through this function
 * (or its `promotions-react` equivalent). Reading `configuration.couponCode`
 * directly is wrong: pre-#1715 promos still have the field populated, but
 * post-#1715 promos do not — so the value you read won't match what the
 * customer sees on the banner.
 *
 * Mirrors promotions-react's internal resolveLocalizedText so the code
 * we apply matches the code the banner displays.
 *
 * `language` is a full BCP-47 tag (e.g. 'de-LU', 'fr-BE') — the exact key
 * fi-admin writes into localizedText entries. Comparisons are case-insensitive:
 * BCP-47 tags are case-insensitive per RFC 5646 §2.1.1, and callers may pass
 * the region subtag lowercased ('de-lu'). A case-sensitive match silently
 * failed for multi-locale countries (Belgium, Luxembourg), falling back to the
 * country default and applying the wrong locale's coupon.
 *
 * Step 2 keeps backwards compatibility with callers that pass a bare
 * two-letter code ('de') or a different-region tag ('de-DE'): within a country
 * the base language subtag is unique, so it resolves to that country's German
 * entry ('de-LU') instead of the (French) country default.
 *
 * Resolution order:
 *   1. Exact match on (language, country)
 *   2. Base-language match within the country (subtag truncation)
 *   3. `defaultForCountry: true` entry for the same country
 *   4. Any entry for the same country
 *   5. Top-level couponCode (DEPRECATED — only reached by legacy data)
 */
export function resolvePromotionCouponCode(
  config: ISimplePromotionConfiguration,
  language?: string,
  country?: string,
): string | undefined {
  const entries = config.localizedText;
  if (!entries || entries.length === 0) {
    return config.couponCode;
  }

  if (language && country) {
    const exact = entries.find(
      (e) => e.language.toLowerCase() === language.toLowerCase() && e.country.toUpperCase() === country.toUpperCase(),
    );
    if (exact) return exact.couponCode;
  }

  if (language && country) {
    const baseLanguage = language.split('-')[0].toLowerCase();
    const baseMatch = entries.find(
      (e) =>
        e.language.split('-')[0].toLowerCase() === baseLanguage && e.country.toUpperCase() === country.toUpperCase(),
    );
    if (baseMatch) return baseMatch.couponCode;
  }

  if (country) {
    const countryDefault = entries.find(
      (e) => e.country.toUpperCase() === country.toUpperCase() && e.defaultForCountry,
    );
    if (countryDefault) return countryDefault.couponCode;

    const anyCountry = entries.find((e) => e.country.toUpperCase() === country.toUpperCase());
    if (anyCountry) return anyCountry.couponCode;
  }

  return config.couponCode;
}
